import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { WorkspaceMemberInfo } from '../workspaces/workspacesApi';
import type { BoardColumn } from '../boardColumns/boardColumnsApi';
import type { Label } from '../labels/labelsApi';
import type { Sprint } from '../sprints/sprintsApi';
import RichTextEditor, { RichTextContent } from '../components/RichTextEditor';
import { DeleteButton } from '../components/DeleteButton';
import { PencilIcon } from '../components/icons';
import { type Task, type UpdateTaskRequest } from './tasksApi';

interface TaskCardProps {
  task: Task;
  workspaceId: string;
  columns: BoardColumn[];
  labels: Label[];
  members: WorkspaceMemberInfo[];
  sprints?: Sprint[];
  hideStatusSelect?: boolean;
  showProjectLink?: boolean;
  /** When true, uncommitted edits are saved automatically on unmount */
  autoSave?: boolean;
  onUpdate: (taskId: string, updates: UpdateTaskRequest) => Promise<Task>;
  onSprintChange?: (sprintId: string) => void;
  onDelete?: () => void;
}

export default function TaskCard({
  task,
  workspaceId,
  columns,
  labels,
  members,
  sprints,
  hideStatusSelect,
  showProjectLink,
  autoSave,
  onUpdate,
  onSprintChange,
  onDelete,
}: TaskCardProps) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [editDescription, setEditDescription] = useState(task.description);
  const [editPoints, setEditPoints] = useState(task.points);
  const [editComplexity, setEditComplexity] = useState<number>(task.complexity ?? 0);
  const [editStartDate, setEditStartDate] = useState(task.startDate ?? '');
  const [editEndDate, setEditEndDate] = useState(task.endDate ?? '');
  const [editLabelIds, setEditLabelIds] = useState<string[]>(task.labelIds);
  const [editAssigneeIds, setEditAssigneeIds] = useState<string[]>(task.assigneeIds);

  // Refs so the unmount cleanup can read current values without stale closures
  const editingRef = useRef(false);
  const onUpdateRef = useRef(onUpdate);
  const editStateRef = useRef({ editDescription, editPoints, editComplexity, editStartDate, editEndDate, editLabelIds, editAssigneeIds });

  useEffect(() => { editingRef.current = editing; }, [editing]);
  useEffect(() => { onUpdateRef.current = onUpdate; }, [onUpdate]);
  useEffect(() => {
    editStateRef.current = { editDescription, editPoints, editComplexity, editStartDate, editEndDate, editLabelIds, editAssigneeIds };
  }, [editDescription, editPoints, editComplexity, editStartDate, editEndDate, editLabelIds, editAssigneeIds]);

  // Auto-save on unmount (panel close or task switch)
  useEffect(() => {
    if (!autoSave) return;
    const taskId = task.taskId;
    return () => {
      if (!editingRef.current) return;
      const s = editStateRef.current;
      onUpdateRef.current(taskId, {
        description: s.editDescription,
        points: s.editPoints,
        complexity: s.editComplexity > 0 ? s.editComplexity : undefined,
        startDate: s.editStartDate || '',
        endDate: s.editEndDate || '',
        labelIds: s.editLabelIds,
        assigneeIds: s.editAssigneeIds,
      }).catch(() => {});
    };
  }, []); // intentionally empty — runs only on mount/unmount

  const taskLabels = labels.filter(label => task.labelIds.includes(label.labelId));
  const taskAssignees = members.filter(member => task.assigneeIds.includes(member.userId));
  const borderColor = taskLabels[0]?.color ?? '#ccc';
  const currentColumnName = columns.find(c => c.columnId === task.status)?.name ?? task.status;

  function startEdit() {
    setEditDescription(task.description);
    setEditPoints(task.points);
    setEditComplexity(task.complexity ?? 0);
    setEditStartDate(task.startDate ?? '');
    setEditEndDate(task.endDate ?? '');
    setEditLabelIds(task.labelIds);
    setEditAssigneeIds(task.assigneeIds);
    setError('');
    setEditing(true);
  }

  async function handleStatusChange(status: string) {
    setError('');
    try {
      await onUpdate(task.taskId, { status });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update task');
    }
  }

  function toggleLabel(labelId: string) {
    setEditLabelIds(prev => prev.includes(labelId) ? prev.filter(id => id !== labelId) : [...prev, labelId]);
  }

  function toggleAssignee(userId: string) {
    setEditAssigneeIds(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);
  }

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      await onUpdate(task.taskId, {
        description: editDescription,
        points: editPoints,
        complexity: editComplexity > 0 ? editComplexity : undefined,
        startDate: editStartDate || '',
        endDate: editEndDate || '',
        labelIds: editLabelIds,
        assigneeIds: editAssigneeIds,
      });
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update task');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="task-card" style={{ borderLeft: `4px solid ${borderColor}` }}>

      {/* Row 1: Title · complexity · points · trash */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
        <strong style={{ flex: 1, fontSize: 14, lineHeight: 1.4 }}>{task.title}</strong>
        {task.complexity != null && task.complexity > 0 && (
          <span className="badge" title="Complexity" style={{ background: '#7c3aed', color: '#fff', flexShrink: 0 }}>
            C{task.complexity}
          </span>
        )}
        {task.points > 0 && (
          <span className="badge" style={{ flexShrink: 0 }}>
            {task.points} pt{task.points === 1 ? '' : 's'}
          </span>
        )}
        {onDelete && <DeleteButton onConfirm={onDelete} title="Delete task" />}
      </div>

      {/* Row 2: Labels */}
      {taskLabels.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
          {taskLabels.map(label => (
            <span key={label.labelId} style={{ fontSize: 11, color: '#fff', background: label.color, borderRadius: 12, padding: '2px 8px' }}>
              {label.name}
            </span>
          ))}
        </div>
      )}

      {/* Row 3: Assignees */}
      {taskAssignees.length > 0 && (
        <p className="muted" style={{ margin: '4px 0 0', fontSize: 12 }}>
          {taskAssignees.map(m => m.displayName).join(', ')}
        </p>
      )}

      {/* Dates (view mode) */}
      {!editing && (task.startDate || task.endDate) && (
        <p className="muted" style={{ margin: '2px 0 0', fontSize: 11 }}>
          {task.startDate && task.endDate
            ? `${task.startDate} → ${task.endDate}`
            : task.startDate ? `Start: ${task.startDate}` : `Due: ${task.endDate}`}
        </p>
      )}

      {error && <p className="error" style={{ margin: '4px 0 0', fontSize: 12 }}>{error}</p>}

      {/* Row 4: Status · sprint · project · edit button */}
      <div className="toolbar--split" style={{ marginTop: 8 }}>
        <div className="toolbar" style={{ gap: 4, flexWrap: 'wrap' }}>
          {!hideStatusSelect && columns.length > 0 ? (
            <select value={task.status} onChange={e => handleStatusChange(e.target.value)} style={{ fontSize: 12 }}>
              {columns.map(col => (
                <option key={col.columnId} value={col.columnId}>{col.name}</option>
              ))}
            </select>
          ) : (
            <span className="muted" style={{ fontSize: 12 }}>{currentColumnName}</span>
          )}
          {task.sprintId && (
            <Link to={`/workspaces/${workspaceId}/sprints/${task.sprintId}/board`} style={{ fontSize: 12 }}>↗ Sprint</Link>
          )}
          {showProjectLink && (
            <Link to={`/workspaces/${workspaceId}/projects/${task.projectId}`} style={{ fontSize: 12 }}>↗ Project</Link>
          )}
        </div>
        <button
          type="button"
          className="icon-btn"
          title={editing ? 'Cancel edit' : 'Edit task'}
          onClick={() => editing ? setEditing(false) : startEdit()}
        >
          <PencilIcon />
        </button>
      </div>

      {/* Sprint assignment (when sprints list is provided) */}
      {sprints !== undefined && (
        <div style={{ marginTop: 6 }}>
          <select value={task.sprintId ?? ''} onChange={e => onSprintChange?.(e.target.value)} style={{ width: '100%', fontSize: 12 }}>
            <option value="">No sprint</option>
            {sprints.map(sprint => (
              <option key={sprint.sprintId} value={sprint.sprintId}>{sprint.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Description — shown below status row when not editing */}
      {!editing && task.description && (
        <div style={{ marginTop: 8 }}>
          <RichTextContent html={task.description} />
        </div>
      )}

      {/* Edit section */}
      {editing && (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--color-border)', display: 'flex', flexDirection: 'column', gap: 8 }}>

          {/* Points · complexity · dates */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12 }}>
              Points
              <input
                type="number"
                min={0}
                value={editPoints}
                onChange={e => setEditPoints(Math.max(0, Number(e.target.value)))}
                style={{ width: 64 }}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12 }}>
              Complexity
              <select value={editComplexity} onChange={e => setEditComplexity(Number(e.target.value))} style={{ width: 110 }}>
                <option value={0}>—</option>
                <option value={1}>1 — Trivial</option>
                <option value={2}>2 — Simple</option>
                <option value={3}>3 — Medium</option>
                <option value={4}>4 — Complex</option>
                <option value={5}>5 — Unknown</option>
              </select>
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12 }}>
              Start
              <input type="date" value={editStartDate} onChange={e => setEditStartDate(e.target.value)} style={{ width: 130 }} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12 }}>
              End
              <input type="date" value={editEndDate} onChange={e => setEditEndDate(e.target.value)} style={{ width: 130 }} />
            </label>
          </div>

          {/* Labels */}
          {labels.length > 0 && (
            <fieldset style={{ border: '1px solid var(--color-border)', borderRadius: 4, padding: 8 }}>
              <legend style={{ fontSize: 12 }}>Labels</legend>
              {labels.map(label => (
                <label key={label.labelId} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                  <input type="checkbox" checked={editLabelIds.includes(label.labelId)} onChange={() => toggleLabel(label.labelId)} />
                  <span style={{ display: 'inline-block', width: 10, height: 10, background: label.color, borderRadius: 2 }} />
                  {label.name}
                </label>
              ))}
            </fieldset>
          )}

          {/* Assignees */}
          {members.length > 0 && (
            <fieldset style={{ border: '1px solid var(--color-border)', borderRadius: 4, padding: 8 }}>
              <legend style={{ fontSize: 12 }}>Assignees</legend>
              {members.map(member => (
                <label key={member.userId} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                  <input type="checkbox" checked={editAssigneeIds.includes(member.userId)} onChange={() => toggleAssignee(member.userId)} />
                  {member.displayName}
                </label>
              ))}
            </fieldset>
          )}

          {/* Save / Cancel */}
          <div className="toolbar">
            <button type="button" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button type="button" onClick={() => setEditing(false)}>Cancel</button>
          </div>

          {/* Description editor — at the bottom */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
            <span>Description</span>
            <RichTextEditor value={editDescription} onChange={setEditDescription} />
          </div>
        </div>
      )}

    </div>
  );
}
